<template>
    <div class="download-index is-exporting" id="download-export-capture">
        <div class="download-header">
            <div class="download-header-title">{{ displayData.title }}</div>
            <div class="download-header-time">数据统计时间：{{ displayData.statistics_time }}</div>
            <div class="download-header-overview">
                <div class="download-header-overview-one">
                    <div class="num">{{ overview.personCount }}</div>
                    <div class="label">参与人数</div>
                </div>
                <div class="download-header-overview-one">
                    <div class="num">{{ overview.schoolCount }}</div>
                    <div class="label">参与学校</div>
                </div>
                <div class="download-header-overview-one">
                    <div class="num">{{ overview.classCount }}</div>
                    <div class="label">参与班级</div>
                </div>
            </div>
        </div>
        <div class="download-body">
            <div class="download-school-card" v-for="(school, i) in selectedSchools" :key="school.school_id"
                :style="i >= MAX_EXPORT_SCHOOL_COUNT ? 'display: none' : ''">
                <div class="download-school-card-head">
                    <div class="name">{{ school.school_name || school.name }}</div>
                    <div class="person">
                        <span class="num">{{ school.course_person_count }}</span>
                        <span class="unit">人</span>
                    </div>
                </div>
                <div class="download-school-card-desc" v-if="school.course_person_count > 0">{{ school.grade_count
                    }}个年级 {{ school.class_count }}个班级</div>
                <!-- 导出层默认渲染全部班级行，无展开按钮，与日常列表 DOM 结构保持一致 -->
                <table class="download-grade-table" v-if="school.course_person_count > 0"
                    v-for="(chunk, ci) in chunkGrades(school.list)" :key="ci">
                    <thead>
                        <tr class="table-row table-head">
                            <th class="cell cell-class">班级</th>
                            <th class="cell" v-for="grade in chunk" :key="grade.name">{{ grade.name }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr class="table-row" v-for="row in classRows(chunk)" :key="row">
                            <td class="cell cell-class">{{ row }}</td>
                            <td class="cell" v-for="grade in chunk" :key="grade.name">{{ cellValue(grade, row) }}</td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr class="table-row table-total">
                            <td class="cell cell-class">合计</td>
                            <td class="cell" v-for="grade in chunk" :key="grade.name">{{ cellValue(grade, '合计') }}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
            <div class="download-footer">小盒·教得更好，学得更好</div>
        </div>
    </div>
</template>
<script lang="ts">
import { PropType } from "vue";

// 导出截图专用组件：与日常列表完全分离，默认渲染全部班级行、无展开按钮、无虚拟滚动
const GRADE_COLUMNS_PER_ROW = 6;
const MAX_EXPORT_SCHOOL_COUNT = 5;

interface ClassItem {
    name: string;
    course_person_count: number;
}

interface Grade {
    name: string;
    list: ClassItem[];
}

interface School {
    school_id: number;
    school_name?: string;
    name?: string;
    course_person_count: number;
    grade_count: number;
    class_count: number;
    list: Grade[];
}

interface Overview {
    personCount: number;
    schoolCount: number;
    classCount: number;
}

interface DisplayData {
    title: string;
    statistics_time: string;
    school_list: School[];
}

export default {
    props: {
        displayData: { type: Object as PropType<DisplayData>, required: true },
        selectedSchools: { type: Array as PropType<School[]>, required: true },
        overview: { type: Object as PropType<Overview>, required: true },
    },
    setup() {
        const gradeOrder = (name: string) => {
            const match = name.match(/\d+|[一二三四五六七八九十]+/);
            if (!match) return Infinity;
            const chineseNumbers = ["一", "二", "三", "四", "五", "六", "七", "八", "九", "十", "十一", "十二"];
            const number = Number(match[0]) || chineseNumbers.indexOf(match[0]) + 1;
            const offset = name.startsWith("初") ? 6 : name.startsWith("高") ? 9 : 0;
            return number ? number + offset : Infinity;
        };
        const chunkGrades = (gradeList: Grade[]) => {
            const chunks: Grade[][] = [];
            const list = [...(gradeList || [])].sort((a, b) => gradeOrder(a.name) - gradeOrder(b.name));
            for (let i = 0; i < list.length; i += GRADE_COLUMNS_PER_ROW) {
                chunks.push(list.slice(i, i + GRADE_COLUMNS_PER_ROW));
            }
            return chunks.length ? chunks : [[]];
        };
        const classRows = (chunk: Grade[]) => {
            const names = new Set<string>();
            (chunk || []).forEach((grade: Grade) => {
                (grade.list || [])
                    .filter((cls: ClassItem) => cls.name !== "合计")
                    .forEach((cls: ClassItem) => names.add(cls.name));
            });
            // 与日常列表保持一致的排序：全局按班级号从小到大（不按年级分组，跨年级相同班级号去重）
            return Array.from(names).sort((a: string, b: string) =>
                a.localeCompare(b, "zh-Hans-CN", { numeric: true })
            );
        };
        const cellValue = (grade: Grade, rowName: string) => {
            const cls = (grade.list || []).find((c: ClassItem) => c.name === rowName);
            return cls ? cls.course_person_count : "-";
        };
        return {
            MAX_EXPORT_SCHOOL_COUNT,
            chunkGrades,
            classRows,
            cellValue,
        };
    },
};
</script>
